import { useCallback, useMemo } from 'react';

import { ICombo, IModifierSelection, IOfferDiscount, PickerSelection } from '@rbi-ctg/menu';
import { useRedeemReward } from 'components/cart-item/redeem-reward/use-redeem-reward';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from 'components/product-detail/utils';
import { MenuObjectTypes } from 'enums/menu';
import { useGetUrlSearchParam } from 'hooks/location/use-get-url-search-param';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { selectors, useAppSelector } from 'state/global-state';
import { getRewardPrice } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { useLoyaltyContext } from 'state/loyalty';
import { LoyaltyOffer, LoyaltyReward, isDiscountReward } from 'state/loyalty/types';
import { isDiscountOffer } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useMenuUIContext } from 'state/menu-ui';
import { useOrderContext } from 'state/order';
import { useProductWizardContext } from 'state/product-wizard';
import { useUIContext } from 'state/ui';
import {
  computeTotalWithOfferDiscount,
  getCartEntryBenefit,
  getIndividualItemPrice,
  getItemLoyaltyRewardDiscount,
} from 'utils/cart/helper';
import { isCombo, isItem } from 'utils/menu';
import { IWithVendorConfig, getOfferVendorConfigForPricing } from 'utils/vendor-config';

import { useExcludedDepositValue } from './use-excluded-deposit';

interface ProductPriceConditions {
  shouldIncludeModifications: boolean;
  shouldIncludeQuantity: boolean;
}

export const useProductPrice = (conditions?: ProductPriceConditions) => {
  const { getAvailableRewardFromCartEntry, isAmountOfferDiscountValueAsCents } =
    useLoyaltyContext();
  const { isOffer: isOfferFromMenuContext, pricingFunction } = useMenuContext();
  const { sanityRewardsMap } = useLoyaltyRewardsList();

  const { activeCartEntries } = useMenuUIContext();
  const { cartIdEditing } = useOrderContext();
  const {
    productQuantity,
    selectedProduct: selectedProductFromMenuContext,
    userSelections,
  } = useProductWizardContext();
  const { formatCurrencyForLocale } = useUIContext();

  const { incentiveNotInMenu } = useRedeemReward();
  const rewardRedemptionBenefitIdParam = useGetUrlSearchParam('reward');
  const rewardIdParam = useGetUrlSearchParam('rewardId');
  const offerRedemptionBenefitIdParam = useGetUrlSearchParam('offer');

  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);

  const selectedOfferFromMenuContext = selectedLoyaltyOffer;

  const { shouldIncludeModifications = false, shouldIncludeQuantity = false } = conditions || {};
  const depositPrice = useExcludedDepositValue(selectedProductFromMenuContext?.options ?? []);

  const isdepositIncluded = useCallback(() => {
    const selectedProduct = selectedProductFromMenuContext;

    if (isCombo(selectedProduct)) {
      return true;
    }

    if (isItem(selectedProduct)) {
      return depositPrice > 0;
    }

    return false;
  }, [selectedProductFromMenuContext, depositPrice]);

  const totalPrice = useCallback(
    (
      modifiersInjected?: IModifierSelection[],
      product?: PickerSelection,
      productIsOffer?: boolean,
      offer?: LoyaltyOffer
    ) => {
      const quantity = shouldIncludeQuantity ? productQuantity : 1;

      const selectedProduct = product || selectedProductFromMenuContext;

      const baseTotal = pricingFunction(selectedProduct, quantity);

      const isOffer = productIsOffer || isOfferFromMenuContext;

      const selectedOffer = offer || selectedOfferFromMenuContext;

      const isOfferContainingDiscount = selectedOffer ? isDiscountOffer(selectedOffer) : false;

      let total = baseTotal;

      if (isOffer && !isOfferContainingDiscount) {
        const vendorConfigs = getOfferVendorConfigForPricing(selectedOffer);
        const price = !!vendorConfigs
          ? pricingFunction({ vendorConfigs } as IWithVendorConfig, quantity, modifiersInjected)
          : 0;
        total = price || selectedOffer?.offerPrice || 0;
      }

      if (shouldIncludeModifications) {
        const comboSlotSelections = transformUserSelectionsComboToComboSlotSelections(
          userSelections.comboSlot,
          selectedProduct as ICombo
        );
        const modifierSelections =
          transformUserSelectionsModifierToModifierSelection(userSelections);
        const extraTotal =
          pricingFunction(selectedProduct, quantity, modifierSelections, comboSlotSelections) -
          baseTotal;

        total += extraTotal;
      }
      return total;
    },
    [
      isOfferFromMenuContext,
      pricingFunction,
      productQuantity,
      selectedOfferFromMenuContext,
      selectedProductFromMenuContext,
      shouldIncludeModifications,
      shouldIncludeQuantity,
      userSelections,
    ]
  );

  const discountPrice = useCallback(() => {
    // TODO: Verify if we need to use activeCartEntries here rather than just the menu item
    const cartEntry = getCartEntryBenefit(
      activeCartEntries,
      getAvailableRewardFromCartEntry,
      rewardRedemptionBenefitIdParam
    );

    if (offerRedemptionBenefitIdParam && selectedLoyaltyOffer) {
      const isOfferDiscount = isDiscountOffer(selectedLoyaltyOffer);
      const offerDiscount = selectedLoyaltyOffer?.incentives?.[0] as IOfferDiscount;

      if (isOfferDiscount && offerDiscount) {
        let mainItem;
        if (offerDiscount.discountProduct?._type === MenuObjectTypes.ITEM) {
          mainItem = offerDiscount.discountProduct;
        } else {
          mainItem = (offerDiscount.discountProduct as unknown as ICombo).mainItem;
        }

        const offerIncentivePrice = pricingFunction(mainItem as IWithVendorConfig, 1);

        const discountedAmount = computeTotalWithOfferDiscount(
          offerIncentivePrice,
          offerDiscount,
          isAmountOfferDiscountValueAsCents
        );

        return offerIncentivePrice - discountedAmount;
      }
    }

    if (!cartEntry) {
      return 0;
    }

    let discount = 0;
    // from loyalty redemption
    if (rewardRedemptionBenefitIdParam) {
      if (rewardIdParam) {
        const sanityReward = sanityRewardsMap?.[rewardIdParam];
        const isRewardDiscount = isDiscountReward(sanityReward);
        const rewardPrice = getRewardPrice(sanityReward as LoyaltyReward);
        const rewardDiscount = sanityReward?.incentives?.[0] as IOfferDiscount;

        if (isRewardDiscount && rewardDiscount) {
          let mainItem;
          if (rewardDiscount.discountProduct?._type === MenuObjectTypes.ITEM) {
            mainItem = rewardDiscount.discountProduct;
          } else {
            mainItem = (rewardDiscount.discountProduct as unknown as ICombo).mainItem;
          }

          const rewardIncentivePrice = pricingFunction(mainItem as IWithVendorConfig, 1);

          const discountedRewardPrice = computeTotalWithOfferDiscount(
            rewardIncentivePrice,
            rewardDiscount,
            isAmountOfferDiscountValueAsCents
          );

          return rewardIncentivePrice - discountedRewardPrice;
        }

        if (rewardPrice) {
          return getIndividualItemPrice({ cartEntry }) - rewardPrice;
        }
      }

      discount = incentiveNotInMenu
        ? (cartEntry.price ?? 0)
        : getIndividualItemPrice({ cartEntry });
    } else if (cartIdEditing) {
      discount = getItemLoyaltyRewardDiscount({
        cartEntry,
        appliedLoyaltyRewards,
        isAmountOfferDiscountValueAsCents,
      });
    }

    return discount;
  }, [
    activeCartEntries,
    appliedLoyaltyRewards,
    cartIdEditing,
    getAvailableRewardFromCartEntry,
    incentiveNotInMenu,
    isAmountOfferDiscountValueAsCents,
    offerRedemptionBenefitIdParam,
    pricingFunction,
    rewardIdParam,
    rewardRedemptionBenefitIdParam,
    sanityRewardsMap,
    selectedLoyaltyOffer,
  ]);

  const formattedProductPrice = useMemo(() => {
    const totalWithDiscount = incentiveNotInMenu ? 0 : Math.max(totalPrice() - discountPrice(), 0);
    return formatCurrencyForLocale(totalWithDiscount);
  }, [discountPrice, formatCurrencyForLocale, incentiveNotInMenu, totalPrice]);

  return {
    isdepositIncluded,
    depositPrice,
    totalPrice,
    discountPrice,
    formattedProductPrice,
  };
};
